import context from '@/context'
import { push } from 'react-router-redux'
import { allowErrorDialog } from '@/utils/fetch'
import { actionCreators as dialogActions } from '@/store/Dialogs'
import { dialogTypes } from '@/dialogs'
import { dispatchError } from '@/utils/serverErrorHandler'
import getComponentKey from '@/utils/getComponentKey'

export const initialState = {
  changeUserStateFetching: false,
  changeUserStateResult: null,
  resetPasswordFetching: false,
  lastReloadAt: getComponentKey(),
  userCreating: false,
  userCreated: false,
  user: {},
  error: null,
  userFetching: false,
  userFetched: false,
  institutionChanging: false,
  userJobsComponentKey: getComponentKey(),
  userJobSaving: false,
  userRolesFetching: false,
  userRolesItems: [],
  internalLastReloadAt: getComponentKey(),
  externalLastReloadAt: getComponentKey(),
  resetTwoFactorFetching: false
}

export const actionTypes = {
  changeUserStateFetching: 'userManagement/changeUserStateFetching',
  changeUserStateFetched: 'userManagement/changeUserStateFetched',

  resetPasswordFetching: 'userManagement/resetPasswordFetching',
  resetPasswordFetched: 'userManagement/resetPasswordFetched',

  userCreating: 'userManagement/userCreating',
  userCreated: 'userManagement/userCreated',

  userFetching: 'userManagement/userFetching',
  userFetched: 'userManagement/userFetched',

  addToInstitution: 'userManagement/addToInstitution',
  addedToInstitution: 'userManagement/addedToInstitution',

  removeFromInstitution: 'userManagement/removeFromInstitution',
  removedFromInstitution: 'userManagement/removedFromInstitution',

  updateUserDto: 'userManagement/updateUserDto',
  resetState: 'userManagement/createUserDto',

  userDelete: 'userManagement/userDelete',
  userDeleted: 'userManagement/userDeleted',

  userApprove: 'userManagement/userApprove',
  userApproved: 'userManagement/userApproved',

  userJobSaving: 'userManagement/userJobSaving',
  userJobSaved: 'userManagement/userJobSaved',

  internalRolesUpdated: 'userManagement/internalRolesUpdated',
  externalRolesUpdated: 'userManagement/externalRolesUpdated',

  resetTwoFactorFetching: 'userManagement/resetTwoFactorFetching',
  resetTwoFactorFetched: 'userManagement/resetTwoFactorFetched',

  userError: 'userManagement/userError',
  error: 'userManagement/error'
}

export const actionCreators = {
  setUserState: (dto, reloadUser) => (dispatch) => {
    dispatch({ type: actionTypes.changeUserStateFetching })
    context.api.userManagementService.setUserState(dto, allowErrorDialog)
      .then(response => {
        dispatch({ type: actionTypes.changeUserStateFetched, data: response })

        if (reloadUser) {
          dispatch(actionCreators.getUser(dto.id))
        }
      })
      .catch(() => dispatch({ type: actionTypes.error, update: { changeUserStateFetching: false } }))
  },

  generatePasswordResetToken: (email) => (dispatch) => {
    dispatch({ type: actionTypes.resetPasswordFetching })
    context.api.userManagementService.generatePasswordResetToken(email, allowErrorDialog)
      .then(response => {
        dispatch({ type: actionTypes.resetPasswordFetched })
        const content = response === true ? 'UsersManagement.ResetPasswordSuccess' : 'UsersManagement.ResetPasswordError'
        dispatch(dialogActions.openDialog({ dialogType: dialogTypes.alert, content, title: 'UsersManagement.ResetPassword' }))
      })
      .catch(() => dispatch({ type: actionTypes.error, update: { passwordResetFetching: false } }))
  },

  updateUserDto: (value) => (dispatch) => {
    dispatch({ type: actionTypes.updateUserDto, data: value })
  },

  resetState: () => (dispatch) => {
    dispatch({ type: actionTypes.resetState })
  },

  createUser: () => (dispatch, getState) => {
    dispatch({ type: actionTypes.userCreating })
    context.api.userManagementService.createUser(getState().usersManagement.user, allowErrorDialog)
      .then(() => {
        dispatch({ type: actionTypes.userCreated })
        dispatch(push('/users'))
      })
      .catch(() => dispatch({ type: actionTypes.error, update: { userCreating: false } }))
  },

  deleteUser: (id) => (dispatch) => {
    dispatch({ type: actionTypes.userDelete })
    context.api.userManagementService.deleteUser(id, allowErrorDialog)
      .then(() => {
        dispatch({ type: actionTypes.userDeleted })
        dispatch(push('/users'))
      })
      .catch(() => dispatch({ type: actionTypes.error }))
  },

  approveUser: (id, reloadUser) => (dispatch) => {
    dispatch({ type: actionTypes.userApprove })
    context.api.userManagementService.approveUser(id, allowErrorDialog)
      .then(response => {
        dispatch({ type: actionTypes.userApproved, data: response })

        if (reloadUser) {
          dispatch(actionCreators.getUser(id))
        }
      })
      .catch(() => dispatch({ type: actionTypes.error }))
  },

  getUser: (id) => (dispatch) => {
    dispatch({ type: actionTypes.userFetching })

    context.api.userManagementService.get(id, allowErrorDialog)
      .then(response => {
        dispatch({ type: actionTypes.userFetched, data: response })
      })
      .catch(() => dispatch({ type: actionTypes.error, update: { userFetching: false } }))
  },

  addToInstitution: (userId, institution) => (dispatch) => {
    dispatch({ type: actionTypes.addToInstitution })

    context.api.userManagementService.addToInstitution(userId, institution.id, allowErrorDialog)
      .then(response => {
        dispatch({ type: actionTypes.addedToInstitution, data: response })
      })
      .catch(error => {
        dispatchError(dispatch, error)
        dispatch({ type: actionTypes.userError })
      })
  },

  removeFromInstitution: (userId, institutionId) => (dispatch) => {
    dispatch({ type: actionTypes.removeFromInstitution })

    context.api.userManagementService.removeFromInstitution(userId, institutionId, allowErrorDialog)
      .then(() => {
        dispatch({ type: actionTypes.removedFromInstitution, data: institutionId })
      })
      .catch(() => dispatch({ type: actionTypes.error, update: { institutionChanging: false } }))
  },

  saveUserJob: (input) => (dispatch) => {
    dispatch({ type: actionTypes.userJobSaving })
    context.api.jobsService.saveUserJob(input, allowErrorDialog)
      .then(response => {
        dispatch({ type: actionTypes.userJobSaved })
      })
      .catch(() => dispatch({ type: actionTypes.error, update: { userJobSaving: false } }))
  },

  deleteUserJob: (id) => (dispatch) => {
    dispatch({ type: actionTypes.userJobSaving })
    context.api.jobsService.deleteUserJob(id)
      .then(response => {
        dispatch({ type: actionTypes.userJobSaved })
      })
      .catch(() => dispatch({ type: actionTypes.error, update: { userJobSaving: false } }))
  },

  addUserRole: (userId, roleId, internal) => (dispatch) => {
    return context.api.userManagementService.addUserToRole(userId, roleId, allowErrorDialog)
      .then(() => {
        if (internal) {
          dispatch({ type: actionTypes.internalRolesUpdated })
        } else {
          dispatch({ type: actionTypes.externalRolesUpdated })
        }
      })
  },

  removeUserRole: (userId, roleId, internal) => (dispatch) => {
    return context.api.userManagementService.removeUserFromRole(userId, roleId, allowErrorDialog)
      .then(() => {
        if (internal) {
          dispatch({ type: actionTypes.internalRolesUpdated })
        } else {
          dispatch({ type: actionTypes.externalRolesUpdated })
        }
      })
  },

  resetTwoFactorAuthForUser: (userId) => (dispatch) => {
    dispatch({ type: actionTypes.resetTwoFactorFetching })
    context.api.userManagementService.resetTwoFactorAuthForUser(userId, allowErrorDialog)
      .then(() => {
        dispatch({ type: actionTypes.resetTwoFactorFetched })
        dispatch(actionCreators.getUser(userId))
      })
      .catch(() => dispatch({ type: actionTypes.error, update: { resetTwoFactorFetching: false } }))
  }
}

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.changeUserStateFetching:
      return { ...state, changeUserStateFetching: true, changeUserStateResult: null }
    case actionTypes.changeUserStateFetched:
      return { ...state, changeUserStateFetching: false, changeUserStateResult: action.data, lastReloadAt: getComponentKey() }
    case actionTypes.resetPasswordFetching:
      return { ...state, passwordResetFetching: true, passwordResetResult: null }
    case actionTypes.resetPasswordFetched:
      return { ...state, passwordResetFetching: false, lastReloadAt: getComponentKey() }
    case actionTypes.userCreating:
      return { ...state, userCreating: true, userCreated: false }
    case actionTypes.userCreated:
      return { ...state, userCreating: false, userCreated: true, lastReloadAt: getComponentKey(), user: {} }
    case actionTypes.updateUserDto:
      return { ...state, user: { ...state.user, ...action.data } }
    case actionTypes.resetState:
      return { ...state, user: {}, emailUniqueFetching: false, emailUniqueFetched: false, resetPasswordResult: null, institutionChanging: false }
    case actionTypes.userApprove:
      return { ...state }
    case actionTypes.userApproved:
      return { ...state, lastReloadAt: getComponentKey() }
    case actionTypes.userDelete:
      return { ...state }
    case actionTypes.userDeleted:
      return { ...state, lastReloadAt: getComponentKey() }
    case actionTypes.userFetching:
      return { ...state, userFetching: true, userFetched: false }
    case actionTypes.userFetched:
      return { ...state, userFetching: false, userFetched: true, user: action.data }
    case actionTypes.addToInstitution:
      return { ...state, institutionChanging: true }
    case actionTypes.addedToInstitution:
      return { ...state, institutionChanging: false, user: { ...state.user, allowedInstitutions: [...state.user.allowedInstitutions, action.data] } }
    case actionTypes.removeFromInstitution:
      return { ...state, institutionChanging: true }
    case actionTypes.removedFromInstitution:
      return { ...state, institutionChanging: false, user: { ...state.user, allowedInstitutions: state.user.allowedInstitutions.filter(x => x.id !== action.data) } }
    case actionTypes.userJobSaving:
      return { ...state, userJobSaving: true }
    case actionTypes.userJobSaved:
      return { ...state, userJobSaving: false, userJobsComponentKey: getComponentKey() }
    case actionTypes.internalRolesUpdated:
      return { ...state, internalLastReloadAt: getComponentKey() }
    case actionTypes.externalRolesUpdated:
      return { ...state, externalLastReloadAt: getComponentKey() }
    case actionTypes.resetTwoFactorFetching:
      return { ...state, resetTwoFactorFetching: true }
    case actionTypes.resetTwoFactorFetched:
      return { ...state, resetTwoFactorFetching: false }
    case actionTypes.userError:
      return { ...state, ...action.data, institutionChanging: false }
    case actionTypes.error: return { ...state, ...action.update }

    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/store/usersManagement.js